import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Box, FieldSelect, ModalBody, ModalFoot, ModalHead, TagSelectItem, TagSelectSuggestion, } from '@dixa/lib-shared-habla';
import noop from 'lodash/noop';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { getConfirmButtonVerb } from 'components/powerFlowShared/helpers/getConfirmButtonVerb';
import { getRequestID } from 'components/shared/util/request';
import { withTags } from 'hocs';
import { FlowNodeTypes } from '../types';
import styles from './tags.module.scss';
import { AddTag } from './tags/addTag';
const TAGS_FIELD = 'tags';
const TAGS_PAGE_SIZE = 50;
var View;
(function (View) {
    View["Select"] = "select";
    View["Add"] = "add";
})(View || (View = {}));
function TagsImpl({ node, nodeLabel, tags: { getItemsAsyncStatus, hasNextPage, hasPreviousPage, items, getItems, getItemsById, nextPage, previousPage, searchTermChange, }, replaceNode, triggerClose, }) {
    var _a;
    const tagNames = (_a = node === null || node === void 0 ? void 0 : node.payload) === null || _a === void 0 ? void 0 : _a.tag_ids;
    const [view, setView] = useState(View.Select);
    const [selectedTags, setSelectedTags] = useState([]);
    const [tags, setTags] = useState([]);
    const [newTagName, setNewTagName] = useState('');
    const [errors, setErrors] = useState({});
    const isChanged = useMemo(() => selectedTags.length !== (tagNames === null || tagNames === void 0 ? void 0 : tagNames.length) ||
        selectedTags.some(({ name }) => (tagNames || []).indexOf(name) === -1), [selectedTags, tagNames]);
    // "selectedTags" must be included in the "tags" array
    // otherwise they won't be displayed in the input field
    useEffect(() => {
        const tags = [...selectedTags, ...items];
        const tagIds = tags.map(({ id }) => id);
        const filteredTags = tags.filter(({ id }, index) => tagIds.indexOf(id) === index);
        setTags(filteredTags);
    }, [items, selectedTags]);
    useEffect(() => {
        if (tagNames) {
            getItemsById(tagNames).then(({ data: { tags } }) => {
                setSelectedTags(tags);
                getItems();
            });
        }
        else {
            getItems();
        }
    }, [getItems, getItemsById, tagNames, setSelectedTags]);
    const validate = useCallback(() => {
        const fieldErrors = {};
        if (!selectedTags.length) {
            fieldErrors[TAGS_FIELD] = 'Required - please select at least one tag';
        }
        setErrors(fieldErrors);
        return !Object.keys(fieldErrors).length;
    }, [selectedTags, setErrors]);
    const handleChange = useCallback((selectedTag, checked = true) => {
        searchTermChange();
        if (checked) {
            setSelectedTags((selectedTags) => [...selectedTags, selectedTag]);
            if (Object.keys(errors).length) {
                setErrors({});
            }
            return;
        }
        const tagIndex = selectedTags.findIndex((tag) => tag.name === selectedTag.name);
        if (tagIndex > -1) {
            setSelectedTags((selectedTags) => {
                const newSelectedTags = [...selectedTags];
                newSelectedTags.splice(tagIndex, 1);
                return newSelectedTags;
            });
        }
    }, [errors, searchTermChange, selectedTags, setSelectedTags]);
    const handleCreateTag = useCallback((tagName) => {
        setNewTagName(tagName);
        setView(View.Add);
    }, []);
    const handleCreateTagClose = useCallback(() => {
        setView(View.Select);
        setNewTagName('');
    }, []);
    const handleClear = useCallback(() => {
        setSelectedTags([]);
    }, [setSelectedTags]);
    const handleSaveClick = useCallback(() => {
        if (!validate())
            return;
        const { payload, nodeType } = node;
        const tagNames = selectedTags.map((tag) => tag.name);
        const nextNodeId = nodeType === FlowNodeTypes.ACTION
            ? getRequestID()
            : payload === null || payload === void 0 ? void 0 : payload.next_node_id;
        const nodeOptions = Object.assign(Object.assign({}, node), { nodeType: FlowNodeTypes.TAGS, text: 'Tag', icon: 'i-tags', isRemovable: true, isEditable: true, isAction: false, overlay: [], badges: [], payload: Object.assign(Object.assign({}, payload), { next_node_id: nextNodeId, tag_ids: tagNames }), childIds: [] });
        replaceNode(nodeOptions);
        triggerClose();
    }, [node, replaceNode, selectedTags, triggerClose, validate]);
    return (_jsxs(Box, Object.assign({ width: "600px" }, { children: [_jsx(ModalHead, Object.assign({ className: styles.head, onClose: triggerClose, title: nodeLabel }, { children: _jsx("div", Object.assign({ className: styles.description }, { children: "Automatically apply custom tags to gain insights about your customer conversations (e.g. identify your VIP customers)." })) })), _jsxs("form", Object.assign({ onSubmit: handleSaveClick }, { children: [_jsx(ModalBody, Object.assign({ className: styles.body }, { children: view === View.Select ? (_jsx(FieldSelect, { clearable: true, hideSelectedItems: true, input: {
                                name: TAGS_FIELD,
                                value: selectedTags.map((tag) => tag.id),
                                onChange: noop,
                            }, suggestionComponent: (props) => (_jsx(TagSelectSuggestion, Object.assign({}, props, { onSuggest: handleCreateTag }))), itemComponent: TagSelectItem, items: tags, label: "Select tags", limit: 2, loading: getItemsAsyncStatus === 'loading', meta: { error: errors[TAGS_FIELD] }, multiple: true, onChange: handleChange, onClear: handleClear, onSearch: searchTermChange, pagination: {
                                nextEnable: hasNextPage,
                                previousEnable: hasPreviousPage,
                                onNext: nextPage,
                                onPrevious: previousPage,
                            }, placeholder: "Select one or multiple tags", searchable: true })) : (_jsx(AddTag, { onAddTag: handleChange, onClose: handleCreateTagClose, tagName: newTagName })) })), _jsx(ModalFoot, { cancelOnClick: triggerClose, confirmName: getConfirmButtonVerb(node), confirmProps: { disabled: !isChanged }, confirmOnClick: handleSaveClick })] }))] })));
}
export const Tags = withTags(TagsImpl, { pageSize: TAGS_PAGE_SIZE });
